import { cloudlyLocales, getCloudlySection } from './cloudly-locales.js';
const cloudlyDemo = document.body.classList.contains('cloudly-studio');
let demoLocale = 'en';
if (cloudlyDemo) {
  let savedLocale = '';
  try { savedLocale = localStorage.getItem('cloudly-viewer-language') || ''; } catch (_) {}
  const requestedLocale = new URLSearchParams(location.search).get('lang');
  const preferredLocale = requestedLocale || savedLocale || navigator.language.split('-')[0];
  if (cloudlyLocales[preferredLocale]) demoLocale = preferredLocale;
}
const demoHotspotIds = ['point-1', 'point-2', 'point-3', 'point-5'];
function createCloudlyLanguagePicker(select) {
  const container = select.closest('.language-selected');
  const names = { en: 'English', de: 'Deutsch', nl: 'Nederlands', fr: 'Français', es: 'Español', pl: 'Polski' };
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'language-toggle';
  toggle.setAttribute('aria-haspopup', 'listbox');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', 'cloudly-language-list');
  const list = document.createElement('div');
  list.id = 'cloudly-language-list';
  list.className = 'language-list';
  list.setAttribute('role', 'listbox');
  list.hidden = true;
  const options = [];
  function close(restoreFocus = false) {
    list.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle.focus();
  }
  function sync() {
    const locale = select.value || demoLocale;
    const label = cloudlyLocales[locale].ui[16];
    toggle.innerHTML = `<span>${locale.toUpperCase()}</span><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>`;
    toggle.setAttribute('aria-label', `${label}: ${names[locale]}`);
    list.setAttribute('aria-label', label);
    options.forEach((option) => {
      const selected = option.dataset.locale === locale;
      option.setAttribute('aria-selected', String(selected));
      option.tabIndex = selected ? 0 : -1;
    });
  }
  Object.entries(names).forEach(([locale, name]) => {
    const option = document.createElement('button');
    option.type = 'button';
    option.className = 'language-option';
    option.dataset.locale = locale;
    option.setAttribute('role', 'option');
    option.setAttribute('lang', locale);
    option.innerHTML = `<span>${name}</span><span class="language-code">${locale.toUpperCase()}</span><svg class="language-check" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m3 8 3 3 7-7"/></svg>`;
    option.addEventListener('click', () => {
      select.value = locale;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      close(true);
    });
    options.push(option);
    list.appendChild(option);
  });
  function open() {
    list.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    options.find((option) => option.dataset.locale === select.value)?.focus();
  }
  toggle.addEventListener('click', () => list.hidden ? open() : close());
  toggle.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open(); }
  });
  list.addEventListener('keydown', (event) => {
    const index = options.indexOf(document.activeElement);
    let next = index;
    if (event.key === 'ArrowDown') next = (index + 1) % options.length;
    else if (event.key === 'ArrowUp') next = (index + options.length - 1) % options.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = options.length - 1;
    else if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    else if (event.key === 'Tab') { close(); return; }
    else return;
    event.preventDefault();
    options[next].focus();
  });
  document.addEventListener('pointerdown', (event) => { if (!container.contains(event.target)) close(); });
  container.addEventListener('focusout', (event) => { if (!container.contains(event.relatedTarget)) close(); });
  select.hidden = true;
  container.append(toggle, list);
  sync();
  return { sync };
}

function localizeDemoHotspot(hotspot) {
  const copy = cloudlyLocales[demoLocale].hotspots?.[demoHotspotIds.indexOf(hotspot.eventName)];
  return cloudlyDemo && copy ? { ...hotspot, label: copy[0], summary: copy[1] } : hotspot;
}

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { BokehPass } from 'three/addons/postprocessing/BokehPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { Reflector } from 'three/addons/objects/Reflector.js';

/* ── Pre-warm DRACO & KTX2 decoders (start download immediately) ── */
const _dracoLoader = new DRACOLoader().setDecoderPath(
  '/libs/draco/'
);
_dracoLoader.preload();

const _ktx2Loader = new KTX2Loader().setTranscoderPath(
  '/libs/basis/'
);

/* ── Utility helpers ───────────────────────────────────────── */

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function readSlug() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[parts.length - 1];
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function resolveAssetUrl(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  if (/^(https?:)?\/\//.test(value) || value.startsWith('/')) {
    return value;
  }

  return null;
}

function normalizeViewerModel(data) {
  const page = data.page || {};
  const viewer = data.viewer || {};

  if (viewer.model && typeof viewer.model === 'object') {
    return {
      ...viewer.model,
      glbUrl: resolveAssetUrl(viewer.model.glbUrl),
      environmentHdrUrl: resolveAssetUrl(viewer.model.environmentHdrUrl),
    };
  }

  if (typeof viewer.model === 'string') {
    return {
      glbUrl: resolveAssetUrl(viewer.model),
      environmentHdrUrl:
        page.model && typeof page.model === 'object'
          ? resolveAssetUrl(page.model.environmentHdrUrl)
          : undefined,
    };
  }

  if (page.model && typeof page.model === 'object') {
    return {
      ...page.model,
      glbUrl: resolveAssetUrl(page.model.glbUrl),
      environmentHdrUrl: resolveAssetUrl(page.model.environmentHdrUrl),
    };
  }

  return {};
}



function flattenAssets(assets) {
  const items = [];

  function walk(prefix, value) {
    if (!value) {
      return;
    }
    if (typeof value === 'string') {
      items.push({ label: prefix, value });
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry, index) => walk(`${prefix}[${index}]`, entry));
      return;
    }
    if (typeof value === 'object') {
      Object.entries(value).forEach(([key, entry]) => {
        walk(prefix ? `${prefix}.${key}` : key, entry);
      });
    }
  }

  walk('', assets);
  return items;
}

function getLocaleMap(data, locale) {
  const locales =
    data.content && data.content.locales && typeof data.content.locales === 'object'
      ? data.content.locales
      : {};
  return locales[locale] && typeof locales[locale] === 'object'
    ? locales[locale]
    : {};
}

function normalizeBullets(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function buildFallbackSections(data) {
  const sections = [
    {
      id: '360-view',
      group: '360-view',
      kicker: 'PRODUCT STORY',
      title: '360° View with Clickable Hotspots',
      summary: 'Explore the product from every angle with a full 360-degree rotation with clickable hotspots and product demo animations.',
      highlight: 'Begin your interactive product exploration',
      body:
        data.page?.description ||
        'Rotate the model to inspect every detail. This interactive view gives you a comprehensive look at the product design and build quality.',
      bullets: [
        'Click hotspots to explore key components and features',
        'Rotate and zoom in for close-up product inspection',
        'Open the action tabs below to view photoreal 4K demo animations',
      ],
      animationFrames: { start: 1, end: 180 },
    },
    {
      id: 'precision',
      group: 'precision',
      kicker: 'Capability',
      title: 'Precision Engineering',
      summary: 'Built with exacting tolerances for repeatable, high-accuracy performance.',
      highlight: 'Engineered for Precision',
      body: 'Every component is designed and manufactured to deliver consistent, repeatable results under demanding conditions.',
      bullets: [
        'High-accuracy motion control',
        'Repeatable positioning',
        'Industrial-grade components',
      ],
      animationFrames: { start: 200, end: 500 },
    },
    {
      id: 'customizable',
      group: 'customizable',
      kicker: 'Flexibility',
      title: 'Fully Customizable',
      summary: 'Adapt the configuration to fit your exact production requirements.',
      highlight: 'Tailored to Your Workflow',
      body: 'Modular design allows you to swap end-effectors, adjust reach parameters, and integrate with existing production lines seamlessly.',
      bullets: [
        'Modular end-effector system',
        'Configurable reach and payload',
        'Seamless production-line integration',
      ],
      animationFrames: { start: 520, end: 740 },
    },
  ];
  if (cloudlyDemo) {
    sections.forEach((section) => Object.assign(section, getCloudlySection('en', section.id)));
  }
  return sections;
}

function extractSections(data) {
  const viewer = data.viewer || {};
  const content = data.content || {};
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const hotspotGroups = Array.isArray(viewer.hotspots) ? viewer.hotspots : [];
  const viewerAnimations =
    viewer.animations && typeof viewer.animations === 'object'
      ? viewer.animations
      : {};

  if (!sections.length) {
    return buildFallbackSections(data);
  }

  const mapped = [];
  const seen = new Set();
  for (const section of sections) {
    const id = slugify(section.id || section.slug || section.title || section.label);
    if (!id) {
      continue;
    }
    mapped.push({
      id,
      group: section.hotspotGroup || section.viewerGroup || id,
      kicker: section.kicker || section.category || 'Section',
      title: section.title || section.label || section.id || 'Untitled section',
      summary: section.summary || section.description || 'This section is defined in the page content model.',
      highlight: section.highlight || section.tagline || section.summary || section.title || 'Section detail',
      body: section.body || section.copy || section.text || '',
      bullets: normalizeBullets(section.bullets || section.items),
      animationFrames:
        section.animationFrames ||
        section.animationRange ||
        section.frameRange ||
        section.animation ||
        section.frames ||
        null,
    });
    seen.add(id);
  }

  for (const group of hotspotGroups) {
    const id = slugify(group.groupName || group.name || group.id);
    if (!id || seen.has(id)) {
      continue;
    }
    // Inherit animationFrames from the nearest content section that shares this group,
    // or from the first content section that has frames, so hotspot-group sections
    // don't silently suppress animation playback.
    const matchingSection = mapped.find((s) => s.group === (group.groupName || group.name || id) && s.animationFrames);
    const fallbackSection = mapped.find((s) => s.animationFrames);
    mapped.push({
      id,
      group: group.groupName || group.name || id,
      kicker: 'Hotspot group',
      title: group.label || group.groupName || group.name || 'Viewer group',
      summary: `Contains ${Array.isArray(group.hotspots) ? group.hotspots.length : 0} hotspot(s).`,
      highlight: group.label || group.groupName || 'Viewer state',
      body: '',
      bullets: [],
      animationFrames: matchingSection?.animationFrames || fallbackSection?.animationFrames || null,
    });
  }

  for (const [animationKey, animationValue] of Object.entries(viewerAnimations)) {
    const animationRange = normalizeAnimationFrames(animationValue);
    if (!animationRange) {
      continue;
    }

    const id = slugify(animationKey);
    if (!id || seen.has(id)) {
      continue;
    }

    const label = String(animationKey)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

    mapped.push({
      id,
      group: id,
      kicker: 'Animation',
      title: label,
      summary: `Frames ${animationRange.start}–${animationRange.end}`,
      highlight: label,
      body: '',
      bullets: [],
      animationFrames: animationRange,
    });
    seen.add(id);
  }

  return mapped.length ? mapped : buildFallbackSections(data);
}

function localizeSection(section, data, locale) {
  if (cloudlyDemo) return { ...section, ...getCloudlySection(locale, section.id) };
  const localeMap = getLocaleMap(data, locale);
  const localized = localeMap[section.id];
  const result = { ...section };

  if (localized && typeof localized === 'object') {
    result.kicker = localized.kicker || result.kicker;
    result.title = localized.title || localized.label || result.title;
    result.summary = localized.summary || localized.description || result.summary;
    result.highlight = localized.highlight || localized.tagline || result.highlight;
    result.body = localized.body || localized.copy || localized.text || result.body;
    const bullets = normalizeBullets(localized.bullets || localized.items);
    if (bullets.length) {
      result.bullets = bullets;
    }
  }

  return result;
}

function normalizeAnimationFrames(value) {
  if (!value) {
    return null;
  }

  if (Array.isArray(value) && value.length >= 2) {
    const start = Number(value[0]);
    const end = Number(value[1]);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return { start, end };
    }
    return null;
  }

  if (typeof value === 'string') {
    const compact = value.trim();
    const match = compact.match(/(-?\d+(?:\.\d+)?)\s*[-:,]\s*(-?\d+(?:\.\d+)?)/);
    if (!match) {
      return null;
    }
    const start = Number(match[1]);
    const end = Number(match[2]);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return { start, end };
    }
    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const nested =
    normalizeAnimationFrames(value.animationFrames) ||
    normalizeAnimationFrames(value.animationRange) ||
    normalizeAnimationFrames(value.frameRange) ||
    normalizeAnimationFrames(value.frames) ||
    normalizeAnimationFrames(value.range);
  if (nested) {
    return nested;
  }

  const start = Number(value.start ?? value.from ?? value.startFrame ?? value.min);
  const end = Number(value.end ?? value.to ?? value.endFrame ?? value.max);
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return { start, end };
  }

  return null;
}

function resolveSectionAnimationFrames(section, data, sectionIndex) {
  const direct =
    normalizeAnimationFrames(section?.animationFrames) ||
    normalizeAnimationFrames(section?.animationRange) ||
    normalizeAnimationFrames(section?.frameRange) ||
    normalizeAnimationFrames(section?.animation) ||
    normalizeAnimationFrames(section?.frames);
  if (direct) {
    return direct;
  }

  const viewerAnimations = data?.viewer?.animations;
  if (viewerAnimations && typeof viewerAnimations === 'object') {
    const keyCandidates = [
      section?.id,
      section?.group,
      slugify(section?.group),
      slugify(section?.id),
      slugify(section?.title),
    ].filter(Boolean);

    for (const key of keyCandidates) {
      if (!(key in viewerAnimations)) {
        continue;
      }
      const range = normalizeAnimationFrames(viewerAnimations[key]);
      if (range) {
        return range;
      }
    }

    const animationEntries = Object.entries(viewerAnimations)
      .map(([key, value]) => ({ key, range: normalizeAnimationFrames(value) }))
      .filter((entry) => Boolean(entry.range));

    if (
      Number.isInteger(sectionIndex) &&
      sectionIndex >= 0 &&
      sectionIndex < animationEntries.length
    ) {
      return animationEntries[sectionIndex].range;
    }

    const genericFallbackKeys = ['all', 'default', 'rotation', '360'];
    for (const key of genericFallbackKeys) {
      if (!(key in viewerAnimations)) {
        continue;
      }
      const range = normalizeAnimationFrames(viewerAnimations[key]);
      if (range) {
        return range;
      }
    }
  }

  const interactionRules = Array.isArray(data?.viewer?.interactionRules)
    ? data.viewer.interactionRules
    : [];
  const sectionTokens = new Set(
    [
      section?.id,
      section?.group,
      section?.title,
      slugify(section?.id),
      slugify(section?.group),
      slugify(section?.title),
    ]
      .filter(Boolean)
      .map((token) => String(token).toLowerCase())
  );

  for (const rule of interactionRules) {
    const ruleTargets = [
      rule?.sectionId,
      rule?.section,
      rule?.group,
      rule?.hotspotGroup,
      rule?.viewerGroup,
      rule?.name,
      rule?.id,
    ]
      .filter(Boolean)
      .map((token) => String(token).toLowerCase());
    const matches = !ruleTargets.length || ruleTargets.some((token) => sectionTokens.has(token));

    if (!matches) {
      continue;
    }

    const range =
      normalizeAnimationFrames(rule?.animationFrames) ||
      normalizeAnimationFrames(rule?.animationRange) ||
      normalizeAnimationFrames(rule?.frameRange) ||
      normalizeAnimationFrames(rule?.animation) ||
      normalizeAnimationFrames(rule?.frames) ||
      normalizeAnimationFrames(rule);
    if (range) {
      return range;
    }
  }

  return null;
}

function extractPois(data, sections) {
  const hotspotGroups = Array.isArray(data.viewer?.hotspots) ? data.viewer.hotspots : [];
  const output = [];
  let order = 0;

  for (const group of hotspotGroups) {
    const groupName = group.groupName || group.name || group.id || '';
    const sectionId = slugify(groupName);
    const hotspots = Array.isArray(group.hotspots) ? group.hotspots : [];

    for (const hotspot of hotspots) {
      const eventName = hotspot.eventName || hotspot.name;
      if (!eventName) {
        continue;
      }
      output.push({
        order,
        eventName,
        sectionId,
        group: groupName,
        label: hotspot.label || hotspot.title || hotspot.name || eventName,
        summary: hotspot.summary || hotspot.description || `Hotspot ${order + 1}`,
      });
      order += 1;
    }
  }

  if (!output.length) {
    sections.forEach((section, index) => {
      output.push({
        order: index,
        eventName: `${section.id}-overview`,
        sectionId: section.id,
        group: section.group,
        label: section.title,
        summary: section.highlight || section.summary,
        synthetic: true,
      });
    });
  }

  return output;
}

function initializeViewer(slug, data) {
  const container = document.querySelector('#viewer-view1');
  const canvas = document.querySelector('#viewer-canvas');
  const overlay = document.querySelector('#hotspot-overlay');
  const model = normalizeViewerModel(data);
  const viewerCfg = data.viewer || {};
  const hdriUrl = resolveAssetUrl(model.environmentHdrUrl) || '/assets/ludwikowice_farmland_2k.hdr';

  const loaderOverlay = document.querySelector('#viewer-loader');

  if (!model.glbUrl || !container || !canvas) {
    if (loaderOverlay) loaderOverlay.classList.add('is-hidden');
    return null;
  }

  /* ── Intro: play unveiling video to mask GLB load time ─── */
  // The <video id="intro-video"> is in the HTML so it autoplays the
  // moment the page is parsed, before the JS bundle even finishes
  // downloading. We just hook the dissolve here. The spinner starts
  // out hidden via `.is-deferred` and only reveals if the model is
  // still loading when the video ends.
  let introActive = false;
  let modelReadyDuringIntro = false;
  const introOverlay = document.querySelector('#intro-video');
  if (overlay) overlay.classList.add('is-suppressed');

  if (introOverlay) {
    introActive = true;

    function finishIntro() {
      if (!introActive) return;
      introActive = false;
      introOverlay.classList.add('is-dissolving');
      // Reveal the spinner only if the model is still loading
      if (!modelReadyDuringIntro && loaderOverlay) {
        loaderOverlay.classList.remove('is-deferred');
      }
      introOverlay.addEventListener(
        'transitionend',
        () => {
          introOverlay.remove();
          // Trigger a full re-evaluation so hotspots appear with the
          // correct occlusion state for the current camera angle.
          needsOcclusionFullFlush = true;
          if (overlay) overlay.classList.remove('is-suppressed');
        },
        { once: true }
      );
    }
    introOverlay.addEventListener('ended', finishIntro);
    introOverlay.addEventListener('error', finishIntro);
    // Defensive: if the browser blocked autoplay (rare with muted),
    // try to start it; if that also fails, dismiss the intro so the
    // user isn't stuck behind a black screen.
    const _introPlayPromise = introOverlay.play();
    if (_introPlayPromise && typeof _introPlayPromise.catch === 'function') {
      _introPlayPromise.catch(finishIntro);
    }
  } else if (loaderOverlay) {
    // No intro element in HTML for some reason — show the spinner now.
    loaderOverlay.classList.remove('is-deferred');
  }

  /* ── Three.js renderer ─────────────────────────────────── */
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.toneMapping = THREE.CineonToneMapping;
  renderer.toneMappingExposure = Number.isFinite(Number(viewerCfg.exposure))
    ? Number(viewerCfg.exposure)
    : 1;
  renderer.shadowMap.enabled = false;

  /* ── Scene + camera ────────────────────────────────────── */
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf2f4f8);
  scene.environmentIntensity = 1; // HDRI strength (0.0 = off, 1.0 = default, 2.0 = double)

  const cam = viewerCfg.camera || viewerCfg.model?.camera || {};
  const fov = cam.fov || 25;
  const aspect = container.clientWidth / container.clientHeight;
  const camera = new THREE.PerspectiveCamera(fov, aspect, 0.01, 100);

  // Fixed camera position matching Blender (converted from Z-up to Y-up)
  // Blender: X=1.7916  Y=2.81888  Z=0.685818
  // Three.js: X=1.7916  Y=0.685818  Z=-2.81888
  const INITIAL_CAM_POS = new THREE.Vector3(2.92056, 0.5, -3.51495);
  const INITIAL_CAM_TARGET = new THREE.Vector3(0, 0.65, 0);
  const cameraFit = (ratio) => cloudlyDemo ? Math.max(1, Math.min(1.4, 0.95 / ratio)) : 1;
  let currentCameraFit = cameraFit(aspect);
  INITIAL_CAM_POS.sub(INITIAL_CAM_TARGET).multiplyScalar(currentCameraFit).add(INITIAL_CAM_TARGET);
  camera.position.copy(INITIAL_CAM_POS);

  /* ── Orbit controls ────────────────────────────────────── */
  const controls = new OrbitControls(camera, canvas);
  // Fixed target — the point the camera looks at (change these to adjust viewing angle)
  controls.target.copy(INITIAL_CAM_TARGET);
  controls.minDistance = 1.25;
  controls.maxDistance = 7;
  controls.minPolarAngle = (5 * Math.PI) / 180;
  controls.maxPolarAngle = (90 * Math.PI) / 180;
  controls.enablePan = cam.enablePan ?? false;
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.update();

  /* ── Lighting ──────────────────────────────────────────── */
  // const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
  // scene.add(ambientLight);

  const dirLight = new THREE.DirectionalLight(0xffffff, 1);
  dirLight.position.set(0.75, 8, 0.866);
  dirLight.castShadow = false;
  scene.add(dirLight);


  /* ── Environment map (HDR / fallback RoomEnvironment) ─── */
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader();

  const neutralEnv = pmremGenerator.fromScene(new RoomEnvironment()).texture;
  scene.environment = neutralEnv;

  const rgbeLoader = new RGBELoader();

  if (hdriUrl) {
    rgbeLoader.load(hdriUrl, (texture) => {
      const envMap = pmremGenerator.fromEquirectangular(texture).texture;
      texture.dispose();
      scene.environment = envMap;
    }, undefined, () => {
      console.warn('Failed to load HDR:', hdriUrl);
    });
  }

  /* ── Post-processing (Bokeh + SMAA) ────────────────────── */
  const rtParams = {
    format: THREE.RGBAFormat,
    type: THREE.HalfFloatType,
  };
  const renderTarget = new THREE.WebGLRenderTarget(
    container.clientWidth * renderer.getPixelRatio(),
    container.clientHeight * renderer.getPixelRatio(),
    { ...rtParams }
  );
  const composer = new EffectComposer(renderer, renderTarget);
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const gtaoPass = new GTAOPass(scene, camera, container.clientWidth, container.clientHeight);
  gtaoPass.output = GTAOPass.OUTPUT.Default;
  gtaoPass.updateGtaoMaterial({
    radius: 0.125,
    distanceExponent: 2,
    thickness: 2,
    scale: 1,
    samples: 16,
  });
  // gtaoPass.updatePdMaterial({
  //   lumaPhi: 10,
  //   depthPhi: 2,
  //   normalPhi: 3,
  //   radius: 4,
  //   rings: 4,
  //   samples: 16,
  // });
  // composer.addPass(gtaoPass);

  const bokehPass = new BokehPass(scene, camera, {
    focus: 3,
    aperture: 0.0004,
    maxblur: 0.005,
  });
  composer.addPass(bokehPass);

  const outputPass = new OutputPass();
  composer.addPass(outputPass);

  const smaaPass = new SMAAPass(
    container.clientWidth * renderer.getPixelRatio(),
    container.clientHeight * renderer.getPixelRatio()
  );
  composer.addPass(smaaPass);

  /* ── Ground plane — Reflector + white overlay for ~50% model reflection ── */
  // Bottom layer: Reflector renders real mirror reflection of the model
  const reflector = new Reflector(new THREE.PlaneGeometry(150, 150), {
    clipBias: 0.003,
    textureWidth: 512,
    textureHeight: 512,
    color: 0x888888,
  });
  reflector.rotation.x = -Math.PI / 2;
  reflector.position.y = 0;
  scene.add(reflector);

  // Top layer: semi-transparent white to fade the reflection (~50%)
  // const groundOverlay = new THREE.Mesh(
  //   new THREE.PlaneGeometry(10, 10),
  //   new THREE.MeshBasicMaterial({
  //     color: 0xf2f4f8,
  //     transparent: true,
  //     opacity: 0.8,
  //     depthWrite: false,
  //   })
  // );
  // groundOverlay.rotation.x = -Math.PI / 2;
  // groundOverlay.position.y = 0.001;
  // scene.add(groundOverlay);

  // Shadow layer on top (disabled — shadows off)
  // const shadowOverlay = new THREE.Mesh(
  //   new THREE.PlaneGeometry(10, 10),
  //   new THREE.ShadowMaterial({ opacity: 0.35 })
  // );
  // shadowOverlay.rotation.x = -Math.PI / 2;
  // shadowOverlay.position.y = 0.002;aj
  // scene.add(shadowOverlay);

  /* ── GLTFLoader setup ──────────────────────────────────── */
  const loadingManager = new THREE.LoadingManager();
  _ktx2Loader.detectSupport(renderer);

  const gltfLoader = new GLTFLoader(loadingManager)
    .setCrossOrigin('anonymous')
    .setDRACOLoader(_dracoLoader)
    .setKTX2Loader(_ktx2Loader)
    .setMeshoptDecoder(MeshoptDecoder);

  /* ── Animation state ───────────────────────────────────── */
  let mixer = null;
  let clips = [];
  let clock = new THREE.Clock();
  let content = null;
  let modelLoaded = false;
  let pendingFrameRange = null;
  let frameRangeActive = false;
  let frameRangeStart = 0;
  let frameRangeEnd = 0;
  let frameRangeLocalTime = 0;
  const FPS = 30;

  /* ── Hotspot data ───────────────────────────────────────── */
  /**
   * Each entry: { worldPos, parentObject, localPos, group }
   * worldPos is the raw parsed position (used before model loads).
   * Once the model is loaded, parentObject + localPos are resolved
   * so hotspots follow animated meshes / bones.
   */
  const hotspotDefs = [];
  const hotspotElements = [];
  const callbacks = [];
  let hotspotsAttached = false;

  const hotspotGroups = Array.isArray(data.viewer?.hotspots) ? data.viewer.hotspots : [];
  for (const group of hotspotGroups) {
    const hotspots = Array.isArray(group.hotspots) ? group.hotspots : [];
    for (const hotspot of hotspots) {
      if (!hotspot.eventName || !hotspot.position) {
        continue;
      }

      // Parse position string "Xm Ym Zm" → THREE.Vector3
      const posCoords = String(hotspot.position)
        .replace(/m/g, '')
        .trim()
        .split(/\s+/)
        .map(Number);
      const worldPos = new THREE.Vector3(
        posCoords[0] || 0,
        posCoords[1] || 0,
        posCoords[2] || 0
      );

      const button = document.createElement('button');
      button.className = 'hotspot-dot';
      button.dataset.eventName = hotspot.eventName;
      const translatedHotspot = localizeDemoHotspot(hotspot);
      button.dataset.hotspotLabel = translatedHotspot.label || hotspot.title || hotspot.name || hotspot.eventName;
      button.dataset.hotspotSummary = translatedHotspot.summary || hotspot.description || '';
      button.setAttribute('aria-label', translatedHotspot.label || hotspot.eventName);

      const core = document.createElement('span');
      core.className = 'hotspot-dot-core';
      button.appendChild(core);

      // Clicking the dot toggles the callout and label visibility
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        if (button.classList.contains('is-selected')) {
          dismissCallout();
        } else {
          showCallout(button);
          callbacks.forEach((fn) => fn(hotspot.eventName));
        }
      });

      overlay.appendChild(button);
      hotspotDefs.push({
        worldPos,
        parentObject: null,
        localPos: null,
        group: group.groupName || group.name || '',
      });
      hotspotElements.push(button);
    }
  }

  /* ── Hotspot callout (animated line + chat bubble) ──────── */
  let activeCallout = null;
  // Index in hotspotDefs/hotspotElements that the active callout is
  // anchored to. Used to keep the callout positioned in sync with the
  // (possibly animated) dot every frame.
  let activeCalloutIndex = -1;

  function dismissCallout() {
    if (activeCallout) {
      activeCallout.remove();
      activeCallout = null;
    }
    activeCalloutIndex = -1;
    hotspotElements.forEach((el) => el.classList.remove('is-selected'));
  }

  function showCallout(buttonEl) {
    dismissCallout();
    buttonEl.classList.add('is-selected');
    activeCalloutIndex = hotspotElements.indexOf(buttonEl);

    const title = buttonEl.dataset.hotspotLabel || '';
    const summary = buttonEl.dataset.hotspotSummary || '';

    const callout = document.createElement('div');
    callout.className = 'hotspot-callout';

    // Two-segment SVG: first line goes up-right, second branches further right
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('class', 'hotspot-callout-line');
    svg.setAttribute('viewBox', '0 0 260 90');
    svg.style.width = '260px';
    svg.style.height = '90px';
    svg.style.position = 'absolute';
    svg.style.left = '0';
    svg.style.bottom = '0';
    svg.style.overflow = 'visible';

    // Segment 1: from dot upward-right
    const seg1 = document.createElementNS(svgNS, 'line');
    seg1.setAttribute('x1', '0');
    seg1.setAttribute('y1', '86');
    seg1.setAttribute('x2', '20');
    seg1.setAttribute('y2', '40');
    seg1.setAttribute('class', 'callout-seg1');
    svg.appendChild(seg1);

    // Segment 2: from elbow further right
    const seg2 = document.createElementNS(svgNS, 'line');
    seg2.setAttribute('x1', '20');
    seg2.setAttribute('y1', '40');
    seg2.setAttribute('x2', '56');
    seg2.setAttribute('y2', '32');
    seg2.setAttribute('class', 'callout-seg2');
    svg.appendChild(seg2);

    // Small circle at the elbow joint
    const joint = document.createElementNS(svgNS, 'circle');
    joint.setAttribute('cx', '20');
    joint.setAttribute('cy', '40');
    joint.setAttribute('r', '2.5');
    joint.setAttribute('class', 'callout-joint');
    svg.appendChild(joint);

    callout.appendChild(svg);

    // Chat bubble
    const bubble = document.createElement('div');
    bubble.className = 'hotspot-callout-bubble';

    if (title) {
      const h = document.createElement('p');
      h.className = 'hotspot-callout-title';
      h.textContent = title;
      bubble.appendChild(h);
    }
    if (summary) {
      const sep = document.createElement('span');
      sep.className = 'hotspot-callout-sep';
      bubble.appendChild(sep);

      const p = document.createElement('p');
      p.className = 'hotspot-callout-text';
      p.textContent = summary;
      bubble.appendChild(p);
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'hotspot-callout-close';
    closeBtn.setAttribute('aria-label', cloudlyDemo ? cloudlyLocales[demoLocale].ui[18] : 'Close');
    closeBtn.innerHTML = '&#215;';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dismissCallout();
    });
    bubble.appendChild(closeBtn);

    callout.appendChild(bubble);
    // Append to the overlay (sibling of the dots) — NOT inside the dot.
    // The dot has a per-frame `transform` which promotes it to its own
    // compositing layer; a `backdrop-filter` inside that layer samples
    // an empty layer instead of the canvas behind, so the frosted-glass
    // blur looks dead. Living in the overlay lets the blur sample the
    // 3D viewer canvas as intended.
    overlay.appendChild(callout);
    activeCallout = callout;
    // Position immediately; the per-frame loop will keep it in sync
    // with the (possibly animated) dot from now on.
    syncCalloutPosition();
  }

  // Keep the active callout anchored to its dot every frame. We reuse
  // the dot's existing screen-space position (left/top + transform)
  // that updateHotspotPositions() already computed, so this is cheap.
  function syncCalloutPosition() {
    if (!activeCallout || activeCalloutIndex < 0) return;
    const dot = hotspotElements[activeCalloutIndex];
    if (!dot) return;
    // Mirror visibility so the callout fades with its dot when the
    // dot becomes occluded / suppressed / behind-camera.
    const hidden =
      dot.style.display === 'none' ||
      dot.classList.contains('is-occluded');
    activeCallout.style.opacity = hidden ? '0' : '';
    activeCallout.style.pointerEvents = hidden ? 'none' : '';
    // Anchor the callout's bottom-left at the dot center on screen,
    // matching how the original layout was visually arranged when the
    // callout was a child of the dot.
    const sx = dot._screenX;
    const sy = dot._screenY;
    if (typeof sx === 'number' && typeof sy === 'number') {
      activeCallout.style.transform =
        `translate(${sx}px, ${sy}px) translate(0, -100%)`;
    }
  }

  // Dismiss callout when clicking the viewer background
  container.addEventListener('click', (e) => {
    if (e.target === canvas || e.target === overlay) {
      dismissCallout();
    }
  });

  /**
   * After the model is loaded, attach each hotspot to the nearest
   * mesh or bone so it follows animations.
   */
  function attachHotspotsToModel(rootObject) {
    const _box = new THREE.Box3();
    const _closestPt = new THREE.Vector3();

    for (const def of hotspotDefs) {
      let bestObj = rootObject;
      let bestDist = Infinity;

      rootObject.traverse((node) => {
        if (!node.isMesh && !node.isBone) return;

        let d;
        if (node.isMesh && node.geometry) {
          // Use actual mesh bounding box for accurate distance
          node.updateWorldMatrix(true, false);
          _box.setFromBufferAttribute(node.geometry.attributes.position);
          _box.applyMatrix4(node.matrixWorld);
          _box.clampPoint(def.worldPos, _closestPt);
          d = _closestPt.distanceTo(def.worldPos);
        } else {
          const nodeWorld = new THREE.Vector3();
          node.getWorldPosition(nodeWorld);
          d = nodeWorld.distanceTo(def.worldPos);
        }

        if (d < bestDist) {
          bestDist = d;
          bestObj = node;
        }
      });

      // Convert the world position to the attachment object's local space
      bestObj.updateWorldMatrix(true, false);
      const localPos = def.worldPos.clone();
      localPos.applyMatrix4(new THREE.Matrix4().copy(bestObj.matrixWorld).invert());

      def.parentObject = bestObj;
      def.localPos = localPos;
    }
    hotspotsAttached = true;
  }

  /* ── Project hotspots from 3D → 2D every frame ─────────── */
  const projVec = new THREE.Vector3();

  /* ── Hotspot occlusion (cheap "is the GLB in front of me?" test) ──
   *
   * Doing a per-hotspot raycast against every mesh of a complex GLB on
   * every frame is wasteful. We make it affordable with a few tricks:
   *
   *   1. Throttle  – run the occlusion test every N frames (~15 Hz).
   *   2. Idle skip – when the camera hasn't moved AND no animation is
   *                  playing, reuse last frame's result (≈0 cost).
   *   3. Sphere pre-pass – for each candidate mesh, do an analytic
   *                  ray-vs-bounding-sphere test on the *segment*
   *                  [camera → hotspot]. Meshes whose sphere doesn't
   *                  intersect the segment are rejected in ~nanoseconds,
   *                  so we only triangle-raycast the few that might
   *                  actually block the line of sight.
   *   4. Skip self – the mesh the hotspot is glued to would always count
   *                  as an occluder; we exclude it and shorten the ray
   *                  by a small epsilon for safety.
   *   5. firstHitOnly – set on the raycaster so a future drop-in of
   *                  three-mesh-bvh gives a free O(log n) speedup.
   *
   * Visibility is communicated via the `.is-occluded` CSS class which
   * fades opacity smoothly, so the throttled cadence is imperceptible.
   */
  const occlusionRaycaster = new THREE.Raycaster();
  occlusionRaycaster.firstHitOnly = true; // honoured by three-mesh-bvh if loaded
  const _occOrigin = new THREE.Vector3();
  const _occDir = new THREE.Vector3();
  const _occWorld = new THREE.Vector3();
  const _occToCenter = new THREE.Vector3();
  const _occSphere = new THREE.Sphere();
  const _occCamMatrix = new THREE.Matrix4();

  let occlusionMeshes = [];          // populated when GLB loads
  let occlusionFrame = 0;
  let occlusionCursor = 0;           // round-robin index across hotspots
  const OCCLUSION_INTERVAL = 4;      // run roughly every 4 frames (~15 Hz @ 60 fps)
  const OCCLUSION_BUDGET = 1;        // raycast at most N hotspots per tick
  const OCCLUSION_EPSILON = 0.02;    // shrink ray to avoid self-hit on attachment surface

  // Skip occlusion while the user is actively dragging/wheel-zooming the
  // viewer. Recompute everything once on release. This is what kills the
  // remaining lag during rotation: per-frame raycasts → 0 during drag.
  let isInteracting = false;
  let needsOcclusionFlush = false;
  // When true, the next eligible occlusion tick re-tests *every* hotspot
  // in a single pass (bypassing the round-robin budget). Used after the
  // arm-animation rising→falling edge so the visible dots reappear in
  // one synchronized step instead of staggered over several ticks.
  let needsOcclusionFullFlush = false;
  // External hold: buttons (4K, Product Launch) keep hotspots hidden
  // across the full pose-reset → video → dissolve cycle so they never
  // flash in during the crossfade. Counted so overlapping holds work.
  let externalSuppressionHolds = 0;
  function holdHotspotSuppression() {
    externalSuppressionHolds++;
    const ov = document.querySelector('#hotspot-overlay');
    if (ov) ov.classList.add('is-suppressed');
    dismissCallout();
  }
  function releaseHotspotSuppression() {
    if (externalSuppressionHolds > 0) externalSuppressionHolds--;
    if (externalSuppressionHolds === 0) {
      // Trigger a full re-evaluation so the dots come back with the
      // correct occlusion state for the current camera angle.
      needsOcclusionFullFlush = true;
    }
  }

  function markInteractStart() {
    isInteracting = true;
    // Hide hotspots + dismiss any open callout while the user is
    // rotating/zooming. They get re-evaluated and faded back in once
    // the pointer is released (markInteractEnd → full occlusion sweep).
    const ov = document.querySelector('#hotspot-overlay');
    if (ov) ov.classList.add('is-suppressed');
    dismissCallout();
  }
  function markInteractEnd() {
    isInteracting = false;
    needsOcclusionFlush = true; // force a full re-check on the next tick
    // Trigger a synchronous full sweep (bypasses the 1-per-tick budget)
    // so every dot's occlusion state is correct before they re-appear.
    needsOcclusionFullFlush = true;
  }

  canvas.addEventListener('pointerdown', markInteractStart);
  canvas.addEventListener('pointerup', markInteractEnd);
  canvas.addEventListener('pointercancel', markInteractEnd);
  // Wheel zoom is a brief but high-frequency event; treat it as a short
  // interaction window (cleared shortly after the last wheel tick).
  let wheelTimer = null;
  canvas.addEventListener('wheel', () => {
    isInteracting = true;
    if (wheelTimer) clearTimeout(wheelTimer);
    wheelTimer = setTimeout(markInteractEnd, 120);
  }, { passive: true });

  function shouldRunOcclusionThisFrame() {
    occlusionFrame++;
    if (occlusionFrame % OCCLUSION_INTERVAL !== 0) return false;
    if (!modelLoaded || occlusionMeshes.length === 0) return false;

    // A full flush (e.g. arm-animation just stopped) takes priority and
    // ignores the interactive/animation gates so the dots re-appear
    // promptly with correct visibility.
    if (needsOcclusionFullFlush) {
      _occCamMatrix.copy(camera.matrixWorld);
      return true;
    }

    // Skip while orbit-controls is being dragged (zero raycasts → smooth
    // rotation). When the pointer is released we set needsOcclusionFlush
    // so the next eligible tick recomputes everything in one pass.
    if (isInteracting) return false;
    if (isAnimationPlaying()) return false;

    if (needsOcclusionFlush) {
      needsOcclusionFlush = false;
      _occCamMatrix.copy(camera.matrixWorld);
      return true;
    }

    // Otherwise only re-test when the camera actually moved.
    const cameraMoved = !_occCamMatrix.equals(camera.matrixWorld);
    if (!cameraMoved) return false;
    _occCamMatrix.copy(camera.matrixWorld);
    return true;
  }

  function isAnimationPlaying() {
    if (!mixer || !clips.length) return false;
    if (frameRangeActive) return true;
    // resetToInitialPose() pauses the action and drives action.time
    // itself for the duration of the ease — visually it IS animating
    // even though `a.paused` is true, so treat it as playing.
    if (resettingPose) return true;
    // Inspect the mixer's active actions (Three.js exposes them via
    // _actions; this is a cheap and stable read across versions).
    const actions = mixer._actions;
    if (!actions) return false;
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i];
      if (a && a.enabled && !a.paused && a.getEffectiveWeight() > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the GLB blocks the line of sight from the camera
   * to `worldPoint`. `ignoreMesh` is the hotspot's own attachment mesh
   * (we don't want it to count as an occluder).
   */
  function isWorldPointOccluded(worldPoint, ignoreMesh) {
    camera.getWorldPosition(_occOrigin);
    _occDir.copy(worldPoint).sub(_occOrigin);
    const hotspotDist = _occDir.length();
    if (hotspotDist < 1e-4) return false;
    _occDir.multiplyScalar(1 / hotspotDist);

    const maxDist = hotspotDist - OCCLUSION_EPSILON;
    if (maxDist <= 0) return false;

    const radiusBias = OCCLUSION_EPSILON;

    for (let m = 0; m < occlusionMeshes.length; m++) {
      const mesh = occlusionMeshes[m];
      if (mesh === ignoreMesh) continue;
      if (!mesh.visible) continue;

      const geom = mesh.geometry;
      if (!geom) continue;
      if (!geom.boundingSphere) geom.computeBoundingSphere();
      if (!geom.boundingSphere) continue;

      // World-space bounding sphere
      _occSphere.copy(geom.boundingSphere).applyMatrix4(mesh.matrixWorld);

      // Cheap segment ↔ sphere test:
      // project sphere center onto ray, check perpendicular distance.
      _occToCenter.copy(_occSphere.center).sub(_occOrigin);
      const t = _occToCenter.dot(_occDir);
      const r = _occSphere.radius + radiusBias;

      // Sphere is entirely behind the camera or beyond the hotspot? skip.
      if (t < -r || t > maxDist + r) continue;

      const perpSq = _occToCenter.lengthSq() - t * t;
      if (perpSq > r * r) continue;

      // Sphere overlaps the segment → do the real triangle raycast on
      // just this one mesh. Bail out on first hit.
      occlusionRaycaster.set(_occOrigin, _occDir);
      occlusionRaycaster.near = 0;
      occlusionRaycaster.far = maxDist;
      const hits = occlusionRaycaster.intersectObject(mesh, false);
      if (hits.length > 0) return true;
    }
    return false;
  }

  function updateHotspotPositions() {
    // Use the canvas dimensions (matches the renderer viewport)
    const vpW = canvas.clientWidth;
    const vpH = canvas.clientHeight;

    const runOcclusion = shouldRunOcclusionThisFrame();
    const total = hotspotDefs.length;

    // Decide which hotspots get an occlusion raycast this tick.
    // Round-robin OCCLUSION_BUDGET per tick keeps cost ~constant
    // regardless of how many hotspots the page defines. A pending
    // full-flush bypasses the budget so every dot is re-tested in one
    // pass (used right after the arm animation stops).
    let occStart = 0;
    let occCount = 0;
    const isFullFlush = runOcclusion && needsOcclusionFullFlush;
    if (runOcclusion && total > 0) {
      if (isFullFlush) {
        occStart = 0;
        occCount = total;
      } else {
        occCount = Math.min(OCCLUSION_BUDGET, total);
        occStart = occlusionCursor % total;
        occlusionCursor = (occStart + occCount) % total;
      }
    }

    for (let i = 0; i < total; i++) {
      const def = hotspotDefs[i];

      if (hotspotsAttached && def.parentObject && def.localPos) {
        // Transform local → world through the (possibly animated) parent
        _occWorld.copy(def.localPos);
        def.parentObject.updateWorldMatrix(true, false);
        _occWorld.applyMatrix4(def.parentObject.matrixWorld);
      } else {
        // Fallback: use raw world position (before model is loaded)
        _occWorld.copy(def.worldPos);
      }

      projVec.copy(_occWorld).project(camera);

      const x = (projVec.x * 0.5 + 0.5) * vpW;
      const y = (-projVec.y * 0.5 + 0.5) * vpH;
      const behind = projVec.z > 1;

      const el = hotspotElements[i];
      el.style.transform = `translate(${x - 8}px, ${y}px)`;
      el.style.left = '0';
      el.style.top = '0';
      el.style.display = behind ? 'none' : '';
      // Stash screen-space center on the dot itself so the callout
      // (which lives outside the dot in the overlay layer) can mirror
      // its position every frame without re-projecting.
      el._screenX = x;
      el._screenY = y + 8; // dot is 16px tall; center sits 8px below anchor

      // Re-evaluate occlusion only for the hotspots in this tick's
      // round-robin window; the others keep their last class state
      // (CSS fade hides the staggered cadence).
      if (runOcclusion && !behind && occCount > 0) {
        // Index `i` is in this tick's window if it falls in
        // [occStart, occStart+occCount) mod total.
        const offset = (i - occStart + total) % total;
        if (offset < occCount) {
          const occluded = isWorldPointOccluded(_occWorld, def.parentObject);
          if (occluded !== def._occluded) {
            def._occluded = occluded;
            el.classList.toggle('is-occluded', occluded);
          }
        }
      }
    }

    // Full-flush completed in this single pass — clear the flag and
    // un-suppress the overlay so the (re-evaluated) dots fade back in.
    // BUT: only remove the .is-suppressed class if no other reason to
    // keep hotspots hidden is still active. Otherwise we'd unhide them
    // mid-animation (e.g. user rotated the camera while the arm clip
    // is still playing — keep them hidden until the arm also stops).
    if (isFullFlush) {
      needsOcclusionFullFlush = false;
      if (!isInteracting && !isAnimationPlaying() && externalSuppressionHolds === 0) {
        const ov = document.querySelector('#hotspot-overlay');
        if (ov) ov.classList.remove('is-suppressed');
      }
    }

    // Keep the open callout (if any) glued to its hotspot's screen-
    // space position, since it now lives outside the dot.
    syncCalloutPosition();
  }

  /* ── Load glTF model ───────────────────────────────────── */
  gltfLoader.load(
    model.glbUrl,
    (gltf) => {
      const object = gltf.scene || gltf.scenes[0];
      if (!object) {
        console.error('GLB has no scene');
        return;
      }

      object.updateMatrixWorld(true);

      // Compute model bounds (no auto-centering – keep original Blender position)
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());

      // Ground plane stays at y=0 (model origin from Blender)

      // Enable shadows on all meshes
      object.traverse((node) => {
        if (node.isMesh) {
          console.log('[Mesh]', node.name);
          const isCircle = node.name.toLowerCase().includes('circle');
          node.castShadow = !isCircle;
          node.receiveShadow = true;

          // Ensure double-sided for transparent materials
          if (node.material) {
            const mats = Array.isArray(node.material) ? node.material : [node.material];
            mats.forEach((mat) => {
              if (mat.transparent || mat.alphaTest > 0) {
                mat.side = THREE.DoubleSide;
              }
            });
          }
        }
      });

      scene.add(object);
      content = object;

      // Cache the meshes that act as occluders for the hotspot visibility
      // test. Skipping the "circle" helper meshes (they're flat decals on
      // the floor) and any non-mesh nodes keeps the per-hotspot pre-pass
      // tight even on heavy GLBs.
      occlusionMeshes = [];
      object.traverse((node) => {
        if (!node.isMesh || !node.geometry) return;
        if (node.name && node.name.toLowerCase().includes('circle')) return;
        if (!node.geometry.boundingSphere) node.geometry.computeBoundingSphere();
        occlusionMeshes.push(node);
      });

      // Update directional light shadow camera to fit model
      const maxDim = Math.max(size.x, size.y, size.z) * 1.1;
      dirLight.shadow.camera.left = -maxDim;
      dirLight.shadow.camera.right = maxDim;
      dirLight.shadow.camera.top = maxDim;
      dirLight.shadow.camera.bottom = -maxDim;
      dirLight.shadow.camera.updateProjectionMatrix();

      // Animations
      clips = gltf.animations || [];
      if (clips.length) {
        mixer = new THREE.AnimationMixer(object);
      }

      modelLoaded = true;

      // If the unveiling intro is still playing, defer revealing the
      // viewer (incl. the spinner-hide) until the video ends — the
      // intro's dissolve will reveal a fully-ready scene.
      if (introActive) {
        modelReadyDuringIntro = true;
      }

      // Hide loading spinner, show hotspots
      if (loaderOverlay) {
        loaderOverlay.classList.add('is-hidden');
        setTimeout(() => loaderOverlay.remove(), 500);
      }
      if (overlay) overlay.classList.add('is-visible');

      // Attach hotspots to nearest mesh/bone so they follow animations
      attachHotspotsToModel(object);

      if (pendingFrameRange) {
        applyPendingFrameRange();
      } else if (clips.length && !staticPoseActive) {
        // Auto-play first animation (skip if starting in static pose)
        const action = mixer.clipAction(clips[0]);
        action.reset().play();
      } else if (clips.length && staticPoseActive) {
        // Prepare action but keep it paused at frame 0
        const action = mixer.clipAction(clips[0]);
        action.reset().play();
        action.paused = true;
      }

      console.log('[Viewer] Model loaded:', model.glbUrl, '| Animations:', clips.length);
    },
    (progress) => {
      if (progress.total > 0) {
        const pct = Math.round((progress.loaded / progress.total) * 100);
        console.log('[Viewer] Loading...', pct + '%');
      }
    },
    (error) => {
      console.error('[Viewer] Failed to load model:', error);
      if (loaderOverlay) loaderOverlay.classList.add('is-hidden');
    }
  );

  /* ── Render loop ───────────────────────────────────────── */
  let wasAnimationPlaying = false;
  function animate() {
    requestAnimationFrame(animate);

    const dt = clock.getDelta();
    controls.update();

    if (mixer) {
      if (resettingPose) {
        // During pose reset animation, skip — resetToInitialPose drives action.time
      } else if (frameRangeActive && clips.length) {
        // Advance local time within the frame range
        frameRangeLocalTime += dt;
        const duration = Math.max((frameRangeEnd - frameRangeStart) / FPS, 1 / FPS);
        if (frameRangeLocalTime >= duration) {
          frameRangeLocalTime = frameRangeLocalTime % duration;
        }
        // Set the action's time directly — avoids the costly mixer.setTime() reset
        const action = mixer.clipAction(clips[0]);
        action.time = frameRangeStart / FPS + frameRangeLocalTime;
        mixer.update(0);  // apply the pose without advancing internal time
      } else {
        mixer.update(dt);
      }
    }

    // Suppress hotspots while the arm is animating, then re-evaluate
    // occlusion once it stops. Detected on rising/falling edges of
    // isAnimationPlaying() so we touch the DOM at most twice per cycle.
    const playing = isAnimationPlaying();
    if (playing !== wasAnimationPlaying) {
      const ov = document.querySelector('#hotspot-overlay');
      if (playing) {
        // Rising edge: hide every dot and dismiss any open callout —
        // their underlying meshes are about to start moving.
        if (ov) ov.classList.add('is-suppressed');
        dismissCallout();
      } else {
        // Falling edge: keep the overlay suppressed for one more pass
        // and request a synchronous full-occlusion sweep. The sweep,
        // when it runs in updateHotspotPositions(), will clear both
        // flags and un-suppress the overlay so dots fade back in only
        // for the hotspots that are actually visible from this angle.
        needsOcclusionFullFlush = true;
      }
      wasAnimationPlaying = playing;
    }

    bokehPass.uniforms['focus'].value = camera.position.distanceTo(controls.target);

    composer.render();
    updateHotspotPositions();
  }

  animate();

  /* ── Resize handler ────────────────────────────────────── */
  const resizeObserver = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) return;
    const nextFit = cameraFit(w / h);
    const fitRatio = nextFit / currentCameraFit;
    camera.position.sub(controls.target).multiplyScalar(fitRatio).add(controls.target);
    INITIAL_CAM_POS.sub(INITIAL_CAM_TARGET).multiplyScalar(fitRatio).add(INITIAL_CAM_TARGET);
    currentCameraFit = nextFit;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    composer.setSize(w, h);
  });
  resizeObserver.observe(container);

  /* ── Animation frame-range playback ────────────────────── */
  function applyPendingFrameRange() {
    if (!pendingFrameRange) {
      return;
    }
    const next = pendingFrameRange;
    pendingFrameRange = null;
    playFrameRange(next.startFrame, next.endFrame);
  }

  function playFrameRange(startFrame, endFrame) {
    // If static pose is active, just save the range for later resume
    if (staticPoseActive) {
      savedFrameRange = { startFrame, endFrame };
      return;
    }

    if (!modelLoaded) {
      pendingFrameRange = { startFrame, endFrame };
      return;
    }

    if (!clips.length || !mixer) {
      pendingFrameRange = { startFrame, endFrame };
      setTimeout(applyPendingFrameRange, 200);
      return;
    }

    // Ensure an action is active
    const action = mixer.clipAction(clips[0]);
    if (!action.isRunning()) {
      action.reset().play();
    }
    action.paused = false;

    frameRangeActive = true;
    frameRangeStart = startFrame;
    frameRangeEnd = endFrame;
    frameRangeLocalTime = 0;
    // Seek to start by setting action.time directly (cheap, no full mixer reset)
    action.time = startFrame / FPS;
    mixer.update(0);

    console.log(
      'Playing frames', startFrame, '-', endFrame,
      '(', (startFrame / FPS).toFixed(2), 's -', (endFrame / FPS).toFixed(2), 's)'
    );
  }

  function stopAnimation() {
    pendingFrameRange = null;
    frameRangeActive = false;

    if (!modelLoaded || !mixer || !clips.length) {
      return;
    }

    // Resume default autoplay
    const action = mixer.clipAction(clips[0]);
    action.paused = false;
    action.reset().play();
  }

  /* ── Static-pose toggle ────────────────────────────────── */
  let staticPoseActive = false;
  let savedFrameRange = null;
  let savedLocalTime = 0;

  function setStaticPose(enabled) {
    staticPoseActive = enabled;

    if (!modelLoaded || !mixer || !clips.length) return;

    if (enabled) {
      // Freeze — save current range AND the current time offset
      if (frameRangeActive) {
        savedFrameRange = { startFrame: frameRangeStart, endFrame: frameRangeEnd };
        savedLocalTime = frameRangeLocalTime;
      }
      frameRangeActive = false;
      const action = mixer.clipAction(clips[0]);
      action.paused = true;
    } else {
      // Resume from exactly where it stopped
      const action = mixer.clipAction(clips[0]);
      action.paused = false;
      if (savedFrameRange) {
        frameRangeActive = true;
        frameRangeStart = savedFrameRange.startFrame;
        frameRangeEnd = savedFrameRange.endFrame;
        frameRangeLocalTime = savedLocalTime;
        savedFrameRange = null;
      }
    }
  }

  function getStaticPose() {
    return staticPoseActive;
  }

  /* ── Reset model to nearest static pose ─────────────────── */
  const STATIC_POSE_FRAMES = [10, 180, 473, 520, 720];
  let resettingPose = false;

  function resetToInitialPose() {
    if (!modelLoaded || !mixer || !clips.length) return Promise.resolve({ durationMs: 0 });

    // Clear any active frame-range playback
    pendingFrameRange = null;
    frameRangeActive = false;
    savedFrameRange = null;
    savedLocalTime = 0;
    staticPoseActive = true;
    resettingPose = true;

    const action = mixer.clipAction(clips[0]);
    // Capture current time BEFORE any reset
    const currentTimeSec = action.time || 0;

    // Make sure the action is playing so we can drive time manually
    if (!action.isRunning()) {
      action.reset().play();
    }
    action.paused = true;
    // Restore captured time (reset().play() would have zeroed it)
    action.time = currentTimeSec;
    mixer.update(0);

    const currentFrame = currentTimeSec * FPS;

    // Find the closest static-pose frame
    let targetFrame = STATIC_POSE_FRAMES[0];
    let minDist = Math.abs(currentFrame - targetFrame);
    for (const sf of STATIC_POSE_FRAMES) {
      const dist = Math.abs(currentFrame - sf);
      if (dist < minDist) {
        minDist = dist;
        targetFrame = sf;
      }
    }

    const targetTimeSec = targetFrame / FPS;
    const startTimeSec = currentTimeSec;

    // Constant speed: duration proportional to frame distance, clamped 1500–3500ms
    const MS_PER_FRAME = 24;
    const frameDist = Math.abs(currentFrame - targetFrame);
    const durationMs = Math.max(1500, Math.min(frameDist * MS_PER_FRAME, 3500));

    // Already close enough (within 1 frame)
    if (Math.abs(startTimeSec - targetTimeSec) < 1 / FPS) {
      action.time = targetTimeSec;
      mixer.update(0);
      resettingPose = false;
      return Promise.resolve({ durationMs: 0 });
    }

    const startTime = performance.now();

    const promise = new Promise((resolve) => {
      function step(now) {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / durationMs, 1);
        // ease-in-out cubic — smooth acceleration and deceleration
        const ease = t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;

        // Lerp from current time to target time (forward or backward)
        action.time = startTimeSec + (targetTimeSec - startTimeSec) * ease;
        mixer.update(0);

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          action.time = targetTimeSec;
          mixer.update(0);
          resettingPose = false;
          resolve({ durationMs });
        }
      }
      requestAnimationFrame(step);
    });

    // Attach durationMs so callers can read it synchronously
    promise.durationMs = durationMs;
    return promise;
  }

  /* ── Instant reset to frame 0 + initial camera ─────────── */
  function instantReset() {
    // Clear animation state
    pendingFrameRange = null;
    frameRangeActive = false;
    savedFrameRange = null;
    savedLocalTime = 0;
    staticPoseActive = true;
    resettingPose = false;

    if (modelLoaded && mixer && clips.length) {
      const action = mixer.clipAction(clips[0]);
      if (!action.isRunning()) {
        action.reset().play();
      }
      action.time = 0;
      action.paused = true;
      mixer.update(0);
    }

    // Snap camera to initial position
    camera.position.copy(INITIAL_CAM_POS);
    controls.target.copy(INITIAL_CAM_TARGET);
    controls.update();
  }

  /* ── Camera reset (smooth animation back to initial view) ── */
  function resetCamera(durationMs = 800) {
    return new Promise((resolve) => {
      const startPos = camera.position.clone();
      const startTarget = controls.target.clone();
      const startTime = performance.now();

      function step(now) {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / durationMs, 1);
        // ease-in-out cubic — smooth acceleration and deceleration
        const ease = t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;

        camera.position.lerpVectors(startPos, INITIAL_CAM_POS, ease);
        controls.target.lerpVectors(startTarget, INITIAL_CAM_TARGET, ease);
        controls.update();

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      }
      requestAnimationFrame(step);
    });
  }

  /* ── Public API ────────────────────────────────────────── */
  return {
    onHotspot(fn) { callbacks.push(fn); },
    SetVisibleHotspotGroup() {},
    SelectHotspot() {},
    ZoomCamera() {},
    playFrameRange,
    stopAnimation,
    setStaticPose,
    getStaticPose,
    dismissCallout,
    resetCamera,
    resetToInitialPose,
    instantReset,
    holdHotspotSuppression,
    releaseHotspotSuppression,
    getZoomRange() {
      return { min: controls.minDistance, max: controls.maxDistance };
    },
    getZoom() {
      return camera.position.distanceTo(controls.target);
    },
    setZoom(distance) {
      const dir = camera.position.clone().sub(controls.target).normalize();
      camera.position.copy(controls.target).addScaledVector(dir, distance);
      controls.update();
    },
  };
}

function applyViewerGroup(runtime, section) {
  if (!runtime || !section?.group || !runtime.SetVisibleHotspotGroup) {
    return;
  }
  runtime.SetVisibleHotspotGroup(section.group);
}

async function main() {
  const slug = readSlug();
  const response = await fetch(`/api/pages/${encodeURIComponent(slug)}`);
  const data = await response.json();

  if (!response.ok) {
    document.querySelector('#panel-title').textContent = 'Page not found';
    document.querySelector('#panel-highlight').textContent = 'Missing page definition';
    document.querySelector('#panel-summary').textContent = data.error || 'No saved page exists for this slug.';
    return;
  }

  const availableLocales = cloudlyDemo ? Object.keys(cloudlyLocales) : Array.isArray(data.supportedLocales) && data.supportedLocales.length
    ? data.supportedLocales
    : ['en'];
  const sections = extractSections(data);
  const pois = extractPois(data, sections);
  const localeSelect = document.querySelector('#locale-select');
  const sectionNav = document.querySelector('#section-nav');
  const panelKicker = document.querySelector('#panel-kicker');
  const panelTitle = document.querySelector('#panel-title');
  const panelHighlight = document.querySelector('#panel-highlight');
  const panelSummary = document.querySelector('#panel-summary');
  const panelBody = document.querySelector('#panel-body');
  const poiList = document.querySelector('#poi-list');
  const viewerLinks = document.querySelector('#viewer-links');
  const viewerMeta = document.querySelector('#viewer-meta');
  const pageJson = document.querySelector('#page-json');
  const model = normalizeViewerModel(data);

  let currentLocale = cloudlyDemo ? demoLocale : availableLocales[0];
  let activeSectionId = sections[0]?.id || null;
  let activePoiEventName = pois[0]?.eventName || null;
  let runtime = null;

  document.title = cloudlyDemo ? 'Industrial robot | Cloudly Studio' : `${data.title} | Velora`;
  if (cloudlyDemo) {
    document.querySelector('.center-logo-link').href = 'https://cloudlystudio.com/';
    localeSelect.closest('.language-selected').hidden = availableLocales.length < 2;
  }
  pageJson.textContent = JSON.stringify(data, null, 2);

  localeSelect.innerHTML = availableLocales
    .map((locale) => `<option value="${escapeHtml(locale)}">${escapeHtml(locale.toUpperCase())}</option>`)
    .join('');

  const languagePicker = cloudlyDemo ? createCloudlyLanguagePicker(localeSelect) : null;

  function applyCloudlyLocale() {
    if (!cloudlyDemo) return;
    const ui = cloudlyLocales[demoLocale].ui;
    document.documentElement.lang = demoLocale;
    document.title = `${ui[2]} | Cloudly Studio`;
    const home = demoLocale === 'en' ? '/' : `/${demoLocale}/`;
    const contact = { en: '/contact/', de: '/de/kontakt/', nl: '/nl/contact/', fr: '/fr/contact/', es: '/es/contacto/', pl: '/pl/kontakt/' };
    document.querySelector('.center-logo-link').href = 'https://cloudlystudio.com' + home;
    document.querySelector('.studio-link').href = 'https://cloudlystudio.com' + home;
    document.querySelector('.project-link').href = 'https://cloudlystudio.com' + contact[demoLocale];
    localeSelect.value = demoLocale;
    languagePicker?.sync();
    const setText = (selector, value) => {
      const element = document.querySelector(selector);
      if (element) element.textContent = value;
    };
    const setLeadingText = (selector, value) => {
      const element = document.querySelector(selector);
      if (element?.firstChild?.nodeType === Node.TEXT_NODE) element.firstChild.textContent = value + ' ';
    };
    setText('.header-caption', ui[0]);
    setLeadingText('.studio-link', ui[1]);
    setText('.stage-caption span:first-child', `01 / ${ui[2]}`);
    setText('.stage-caption span:last-child', ui[3]);
    setText('.stage-hint', `${ui[4]} · ${ui[5]}`);
    setText('#toggle-launch-demo .cloudly-only', ui[6]);
    setText('.play-copy', ui[7]);
    setText('.pause-copy', ui[8]);
    setText('#toggle-hd-transition .control-copy', ui[9]);
    setLeadingText('#return-to-model', ui[10]);
    setLeadingText('.project-link', ui[11]);
    setLeadingText('.project-link > span', ui[12]);
    setText('.viewer-loader-text', ui[14]);
    document.querySelector('#zoom-slider').setAttribute('aria-label', ui[15]);
    localeSelect.setAttribute('aria-label', ui[16]);
    sectionNav.setAttribute('aria-label', ui[17]);
    document.querySelector('#toggle-launch-demo').setAttribute('aria-label', ui[6]);
    document.querySelector('#toggle-launch-demo').title = ui[6];
    document.querySelector('#toggle-hd-transition').setAttribute('aria-label', ui[9]);
    document.querySelector('#toggle-hd-transition').title = ui[9];
    const playButton = document.querySelector('#toggle-play-pause');
    playButton.setAttribute('aria-label', ui[playButton.classList.contains('is-paused') ? 7 : 8]);
    playButton.title = ui[7] + ' / ' + ui[8];
    document.querySelector('#viewer-canvas').setAttribute('aria-label', `${ui[2]}. ${ui[4]}. ${ui[5]}.`);
    const originals = (data.viewer?.hotspots || []).flatMap((group) => group.hotspots || []);
    document.querySelectorAll('.hotspot-dot').forEach((button) => {
      const original = originals.find((hotspot) => hotspot.eventName === button.dataset.eventName);
      if (!original) return;
      const translated = localizeDemoHotspot(original);
      button.dataset.hotspotLabel = translated.label;
      button.dataset.hotspotSummary = translated.summary;
      button.setAttribute('aria-label', translated.label);
    });
  }

  function getSection() {
    if (!activeSectionId) return sections[0] || null;
    return sections.find((entry) => entry.id === activeSectionId) || sections[0];
  }

  function getPoisForActiveSection() {
    return pois.filter((poi) => poi.sectionId === activeSectionId);
  }

  function syncPoiForSection() {
    if (!activeSectionId) {
      activePoiEventName = null;
      return;
    }
    const sectionPois = getPoisForActiveSection();
    if (!sectionPois.length) {
      activePoiEventName = null;
      return;
    }
    if (!sectionPois.some((poi) => poi.eventName === activePoiEventName)) {
      activePoiEventName = sectionPois[0].eventName;
    }
  }

  function renderMeta() {
    const rows = [
      ['Page', data.title],
      ['Slug', data.slug],
      ['Status', data.status],
      ['Locale', currentLocale.toUpperCase()],
      ['Model', model.glbUrl || 'Not set'],
      ['Environment', model.environmentHdrUrl || 'Not set'],
    ];

    viewerMeta.innerHTML = rows
      .map(
        ([label, value]) => `
          <div class="meta-row">
            <span>${escapeHtml(label)}</span>
            <strong>${escapeHtml(value)}</strong>
          </div>
        `
      )
      .join('');

    const links = [];
    if (model.glbUrl) {
      links.push(`<a href="${escapeHtml(model.glbUrl)}" target="_blank" rel="noreferrer">Open model</a>`);
    }
    if (model.environmentHdrUrl) {
      links.push(`<a href="${escapeHtml(model.environmentHdrUrl)}" target="_blank" rel="noreferrer">Open environment</a>`);
    }
    links.push('<a href="/" rel="noreferrer">Back to admin</a>');
    viewerLinks.innerHTML = links.join('');
  }

  function renderSections() {
    sectionNav.innerHTML = sections
      .map((section, index) => {
        const localized = localizeSection(section, data, currentLocale);
        const selected = section.id === activeSectionId ? ' selected' : '';
        return `<button class="sidebar-button${selected}" data-section-id="${escapeHtml(section.id)}" type="button" aria-pressed="${Boolean(selected)}">${cloudlyDemo ? `<span class="chapter-number">${String(index + 1).padStart(2, '0')}</span>` : ''}<span>${escapeHtml(cloudlyDemo && localized.navTitle ? localized.navTitle : localized.title)}</span>${cloudlyDemo ? '<span class="chapter-status" aria-hidden="true"></span>' : ''}</button>`;
      })
      .join('');

    sectionNav.querySelectorAll('[data-section-id]').forEach((button) => {
      button.addEventListener('click', () => {
        returnToModel?.click();
        activeSectionId = button.dataset.sectionId;
        // Dismiss any open hotspot callout
        if (runtime?.dismissCallout) {
          runtime.dismissCallout();
        }
        // Resume animation when a section is explicitly selected
        if (runtime?.getStaticPose?.()) {
          runtime.setStaticPose(false);
          if (playPauseButton) {
            playPauseButton.classList.remove('is-paused');
          }
        }
        syncPoiForSection();
        renderSections();
        renderPanel();
        renderPois();
      });
    });
  }

  function renderPanel() {
    const section = getSection();
    if (!section) {
      panelKicker.textContent = data.page?.brand || 'Velora';
      panelTitle.textContent = data.title || 'Welcome';
      panelHighlight.textContent = '';
      panelSummary.textContent = 'Select a section below to explore this product.';
      panelBody.innerHTML = '';
      return;
    }
    const localized = localizeSection(section, data, currentLocale);
    panelKicker.textContent = localized.kicker || data.page?.brand || 'Velora';
    panelTitle.textContent = localized.title || data.title;
    panelHighlight.textContent = localized.highlight || data.title;
    panelSummary.textContent = localized.summary || '';

    const bodyBlocks = [];
    if (localized.body) {
      bodyBlocks.push(`
        <section class="panel-block">
          <p>${escapeHtml(localized.body)}</p>
        </section>
      `);
    }
    if (localized.bullets?.length) {
      bodyBlocks.push(`
        <section class="panel-block">
          <p class="panel-block-title">${cloudlyDemo ? cloudlyLocales[currentLocale].ui[13] : 'Key points'}</p>
          <ul>${localized.bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
        </section>
      `);
    }

    panelBody.innerHTML = bodyBlocks.join('');
    applyViewerGroup(runtime, section);

    // Play animation frame range for this section
  const sectionIndex = sections.findIndex((entry) => entry.id === section.id);
  const frameRange = resolveSectionAnimationFrames(section, data, sectionIndex);
    const frameStart = Number(frameRange?.start);
    const frameEnd = Number(frameRange?.end);
    const hasValidFrameRange = Number.isFinite(frameStart) && Number.isFinite(frameEnd) && frameEnd > frameStart;

    if (runtime && hasValidFrameRange) {
      runtime.playFrameRange(frameStart, frameEnd);
    } else if (runtime?.stopAnimation) {
      runtime.stopAnimation();
    }
  }

  function selectPoi(eventName) {
    activePoiEventName = eventName;
    const poi = pois.find((entry) => entry.eventName === eventName);
    if (poi?.sectionId) {
      activeSectionId = poi.sectionId;
    }
    renderSections();
    renderPanel();
    renderPois();

    if (runtime && poi && !poi.synthetic && runtime.SelectHotspot) {
      runtime.SelectHotspot(poi.eventName);
    }
  }

  function renderPois() {
    const sectionPois = getPoisForActiveSection();
    poiList.innerHTML = sectionPois
      .map((poi) => {
        const selected = poi.eventName === activePoiEventName ? ' selected' : '';
        return `
          <button class="button${selected}" type="button" data-poi-event="${escapeHtml(poi.eventName)}">
            <span class="button-label">${escapeHtml(poi.label)}</span>
            <span class="button-caption">${escapeHtml(poi.summary)}</span>
          </button>
        `;
      })
      .join('');

    poiList.querySelectorAll('[data-poi-event]').forEach((button) => {
      button.addEventListener('click', () => {
        selectPoi(button.dataset.poiEvent);
      });
    });
  }

  localeSelect.addEventListener('change', () => {
    currentLocale = localeSelect.value;
    if (cloudlyDemo) {
      demoLocale = currentLocale;
      try { localStorage.setItem('cloudly-viewer-language', demoLocale); } catch (_) {}
      const url = new URL(location.href);
      url.searchParams.set('lang', demoLocale);
      history.replaceState(null, '', url);
      runtime?.dismissCallout?.();
      applyCloudlyLocale();
    }
    renderMeta();
    renderSections();
    renderPanel();
    renderPois();
  });

  const playPauseButton = document.querySelector('#toggle-play-pause');
  const returnToModel = cloudlyDemo ? document.querySelector('#return-to-model') : null;
  if (cloudlyDemo && playPauseButton) {
    const syncPlayLabel = () => {
      const paused = playPauseButton.classList.contains('is-paused');
      playPauseButton.setAttribute('aria-label', cloudlyLocales[demoLocale].ui[paused ? 7 : 8]);
      playPauseButton.setAttribute('aria-pressed', String(!paused));
    };
    new MutationObserver(syncPlayLabel).observe(playPauseButton, { attributes: true, attributeFilter: ['class'] });
    syncPlayLabel();
  }
  if (playPauseButton) {
    playPauseButton.addEventListener('click', () => {
      if (!runtime) return;
      const isStatic = runtime.getStaticPose();
      runtime.setStaticPose(!isStatic);
      playPauseButton.classList.toggle('is-paused', !isStatic);
    });
  }

  /* ── HD transition button ──────────────────────────────── */
  const hdTransitionBtn = document.querySelector('#toggle-hd-transition');
  const viewerContainer = document.querySelector('#viewer-view1');
  let hdOverlay = null;
  let hdActive = false;

  // HD transition video – pick source based on active section index
  const HD_VIDEO_LIST = [
    '/assets/arm_animation.mp4',  // section 0: 360° View
    '/assets/render2.mp4',        // section 1: Precision Engineering
    '/assets/render3.mp4',        // section 2: Fully Customizable
  ];

  if (hdTransitionBtn && viewerContainer) {
    // Pre-create the overlay video (hidden)
    hdOverlay = document.createElement('video');
    hdOverlay.src = HD_VIDEO_LIST[0];
    hdOverlay.muted = true;
    hdOverlay.playsInline = true;
    hdOverlay.preload = 'auto';
    hdOverlay.className = 'hd-transition-overlay';
    viewerContainer.appendChild(hdOverlay);

    // When video ends, smoothly dissolve back to 3D viewer
    function dissolveBackToViewer() {
      if (!hdActive) return;
      if (returnToModel) returnToModel.hidden = true;
      viewerContainer.classList.remove('is-playing-film');
      hdOverlay.classList.add('is-dissolving');
      hdTransitionBtn.classList.remove('is-active');
      hdActive = false;

      // Reset 3D viewer to initial state while dissolve plays
      runtime.instantReset();
      if (playPauseButton) {
        playPauseButton.classList.add('is-paused');
      }

      // After the CSS dissolve transition completes, fully hide the overlay
      const onTransitionEnd = () => {
        hdOverlay.removeEventListener('transitionend', onTransitionEnd);
        hdOverlay.classList.remove('is-visible', 'is-dissolving');
        hdOverlay.pause();
        hdOverlay.currentTime = 0;
        hdTransitionBtn.style.display = '';
        // Release the hotspot suppression hold acquired on click
        runtime.releaseHotspotSuppression?.();
      };
      hdOverlay.addEventListener('transitionend', onTransitionEnd);
    }

    hdOverlay.addEventListener('error', dissolveBackToViewer);
    hdOverlay.addEventListener('ended', () => {
      // Freeze on last frame briefly before dissolving back
      setTimeout(dissolveBackToViewer, 420);
    });

    hdTransitionBtn.addEventListener('click', async () => {
      if (!runtime) return;

      if (hdActive) {
        // Manual dismiss while video is still playing
        dissolveBackToViewer();
        return;
      }

      // Hide hotspots immediately and keep them hidden for the entire
      // pose-reset → video → dissolve cycle (released in onTransitionEnd).
      runtime.holdHotspotSuppression?.();

      // 1. Smoothly reset model to frame 0 AND camera to initial position (in parallel)
      if (playPauseButton) {
        playPauseButton.classList.add('is-paused');
      }
      const posePromise = runtime.resetToInitialPose();
      // Match camera duration to the pose reset so they finish together
      const poseDuration = posePromise.durationMs || 800;
      await Promise.all([
        posePromise,
        runtime.resetCamera(poseDuration),
      ]);

      // 2. Short pause to let the last frame settle
      await new Promise((r) => setTimeout(r, 150));

      // 3. Pick the right video for the active section
      const sectionIndex = sections.findIndex((s) => s.id === activeSectionId);
      const videoSrc = HD_VIDEO_LIST[sectionIndex >= 0 ? sectionIndex : 0] || HD_VIDEO_LIST[0];
      if (hdOverlay.src !== new URL(videoSrc, location.href).href) {
        hdOverlay.src = videoSrc;
        hdOverlay.load();
      }

      // 4. Crossfade to video and play after a short delay
      hdOverlay.currentTime = 0;
      hdOverlay.classList.remove('is-dissolving');
      hdOverlay.classList.add('is-visible');
      hdTransitionBtn.classList.add('is-active');
      hdTransitionBtn.style.display = 'none';
      hdActive = true;
      if (returnToModel) {
        returnToModel.hidden = false;
        returnToModel.onclick = dissolveBackToViewer;
        viewerContainer.classList.add('is-playing-film');
      }
      await new Promise((r) => setTimeout(r, 750));
      if (hdActive) hdOverlay.play().catch(dissolveBackToViewer);
    });
  }

  /* ── Product Launch demo button ───────────────────────── */
  // Mirrors the 4K/HD transition flow, but always plays the same
  // unveiling video regardless of which section is active.
  const launchDemoBtn = document.querySelector('#toggle-launch-demo');
  let launchOverlay = null;
  let launchActive = false;
  const LAUNCH_VIDEO_SRC = '/assets/Unveiling.mp4';

  if (launchDemoBtn && viewerContainer) {
    launchOverlay = document.createElement('video');
    launchOverlay.src = LAUNCH_VIDEO_SRC;
    launchOverlay.muted = true;
    launchOverlay.playsInline = true;
    launchOverlay.preload = 'auto';
    // Reuse the same dissolve/visibility CSS class as the HD overlay
    launchOverlay.className = 'hd-transition-overlay';
    viewerContainer.appendChild(launchOverlay);

    function dissolveLaunchBackToViewer() {
      if (!launchActive) return;
      if (returnToModel) returnToModel.hidden = true;
      viewerContainer.classList.remove('is-playing-film');
      launchOverlay.classList.add('is-dissolving');
      launchDemoBtn.classList.remove('is-active');
      launchActive = false;

      // Reset 3D viewer to initial state while dissolve plays
      runtime.instantReset();
      if (playPauseButton) {
        playPauseButton.classList.add('is-paused');
      }

      const onTransitionEnd = () => {
        launchOverlay.removeEventListener('transitionend', onTransitionEnd);
        launchOverlay.classList.remove('is-visible', 'is-dissolving');
        launchOverlay.pause();
        launchOverlay.currentTime = 0;
        launchDemoBtn.style.display = '';
        runtime.releaseHotspotSuppression?.();
      };
      launchOverlay.addEventListener('transitionend', onTransitionEnd);
    }

    launchOverlay.addEventListener('error', dissolveLaunchBackToViewer);
    launchOverlay.addEventListener('ended', () => {
      // Match HD button: short freeze on last frame before dissolving back
      setTimeout(dissolveLaunchBackToViewer, 420);
    });

    launchDemoBtn.addEventListener('click', async () => {
      if (!runtime) return;

      if (launchActive) {
        // Manual dismiss while video is still playing
        dissolveLaunchBackToViewer();
        return;
      }

      // Hide hotspots for the entire pose-reset → video → dissolve cycle
      runtime.holdHotspotSuppression?.();

      // 1. Smoothly reset model + camera to initial pose (parallel) — same as 4K
      if (playPauseButton) {
        playPauseButton.classList.add('is-paused');
      }
      const posePromise = runtime.resetToInitialPose();
      const poseDuration = posePromise.durationMs || 800;
      await Promise.all([
        posePromise,
        runtime.resetCamera(poseDuration),
      ]);

      // 2. Short pause to let the last frame settle
      await new Promise((r) => setTimeout(r, 150));

      // 3. Crossfade to launch video
      launchOverlay.currentTime = 0;
      launchOverlay.classList.remove('is-dissolving');
      launchOverlay.classList.add('is-visible');
      launchDemoBtn.classList.add('is-active');
      launchDemoBtn.style.display = 'none';
      launchActive = true;
      if (returnToModel) {
        returnToModel.hidden = false;
        returnToModel.onclick = dissolveLaunchBackToViewer;
        viewerContainer.classList.add('is-playing-film');
      }
      await new Promise((r) => setTimeout(r, 750));
      if (launchActive) launchOverlay.play().catch(dissolveLaunchBackToViewer);
    });
  }

  renderMeta();
  renderSections();
  runtime = initializeViewer(slug, data);
  applyCloudlyLocale();

  // Start in static pose — no animation plays until a section is clicked
  if (runtime?.setStaticPose) {
    runtime.setStaticPose(true);
    if (playPauseButton) {
      playPauseButton.classList.add('is-paused');
    }
  }

  /* ── Zoom slider wiring ──────────────────────────────────── */
  const zoomSlider = document.querySelector('#zoom-slider');
  if (zoomSlider && runtime?.getZoomRange) {
    const { min: zMin, max: zMax } = runtime.getZoomRange();

    function distanceToSlider(d) {
      // Invert: slider top = zoomed in (min distance), bottom = zoomed out (max distance)
      return 100 - ((d - zMin) / (zMax - zMin)) * 100;
    }

    function sliderToDistance(v) {
      return zMin + ((100 - v) / 100) * (zMax - zMin);
    }

    // Set initial slider position from current camera distance
    zoomSlider.value = distanceToSlider(runtime.getZoom());

    zoomSlider.addEventListener('input', () => {
      runtime.setZoom(sliderToDistance(Number(zoomSlider.value)));
    });

    // Keep slider in sync when the user scrolls / pinches to zoom
    const syncSlider = () => {
      if (document.activeElement !== zoomSlider) {
        zoomSlider.value = distanceToSlider(runtime.getZoom());
      }
      requestAnimationFrame(syncSlider);
    };
    requestAnimationFrame(syncSlider);
  }

  if (runtime?.onHotspot) {
    runtime.onHotspot((eventName) => {
      const poi = pois.find((entry) => entry.eventName === eventName);
      if (!poi) {
        return;
      }

      // Pause animation when a hotspot is clicked
      if (runtime?.setStaticPose && !runtime.getStaticPose()) {
        runtime.setStaticPose(true);
        if (playPauseButton) {
          playPauseButton.classList.add('is-paused');
        }
      }
    });
  }
  syncPoiForSection();
  renderPanel();
  renderPois();

  if (!runtime) {
    document.querySelector('#panel-summary').textContent = 'Viewer could not be initialized. Check the model URL.';
  }
}

main().catch((error) => {
  document.querySelector('#panel-title').textContent = 'Render error';
  document.querySelector('#panel-highlight').textContent = 'The page failed to load';
  document.querySelector('#panel-summary').textContent = error.message;
});